/**
 * 通知服务（Web 应用版本）
 * 提供通知的创建、查询、标记已读等功能
 */

import { supabase } from '@ext-bookmark/shared/api';
import { getCurrentUser } from '../auth/userService';

export interface Notification {
  id: string;
  user_id: string;
  type: 'group_invite' | 'group_invite_accepted' | 'family_invite' | 'todo_assigned' | 'todo_reminder' | 'group_member_removed' | 'comment_mentioned';
  title: string;
  message: string;
  data?: {
    group_id?: string;
    family_id?: string;
    inviter_id?: string;
    invitee_id?: string;
    invitation_id?: string;
    expires_at?: string; // 邀请过期时间（ISO 8601 格式）
    remover_id?: string;
    todo_id?: string;
    comment_id?: string;
    commenter_id?: string;
    [key: string]: any;
  };
  read: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationServiceError {
  message: string;
  code?: string;
}

/**
 * 创建通知
 */
export async function createNotification(
  userId: string,
  type: Notification['type'],
  title: string,
  message: string,
  data?: Notification['data']
): Promise<{ notification: Notification | null; error: NotificationServiceError | null }> {
  if (!supabase) {
    return {
      notification: null,
      error: { message: 'Supabase 未配置' },
    };
  }

  try {
    const { data: notificationData, error } = await supabase
      .from('notifications')
      .insert({
        user_id: userId,
        type,
        title,
        message,
        data: data || null,
        read: false,
      })
      .select()
      .single();

    if (error) {
      console.error('[Notification] 创建通知失败:', error);
      return {
        notification: null,
        error: {
          message: error.message || '创建通知失败',
          code: error.code,
        },
      };
    }

    return { notification: notificationData, error: null };
  } catch (error: any) {
    console.error('[Notification] 创建通知异常:', error);
    return {
      notification: null,
      error: {
        message: error.message || '创建通知时发生未知错误',
      },
    };
  }
}

/**
 * 获取用户的通知列表
 */
export async function getUserNotifications(
  unreadOnly: boolean = false
): Promise<{ notifications: Notification[]; error: NotificationServiceError | null }> {
  if (!supabase) {
    return {
      notifications: [],
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      notifications: [],
      error: { message: '用户未登录' },
    };
  }

  try {
    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (unreadOnly) {
      query = query.eq('read', false);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[Notification] 获取通知列表失败:', error);
      return {
        notifications: [],
        error: {
          message: error.message || '获取通知列表失败',
          code: error.code,
        },
      };
    }

    return { notifications: data || [], error: null };
  } catch (error: any) {
    console.error('[Notification] 获取通知列表异常:', error);
    return {
      notifications: [],
      error: {
        message: error.message || '获取通知列表时发生未知错误',
      },
    };
  }
}

/**
 * 获取未读通知数量
 */
export async function getUnreadNotificationCount(): Promise<{ count: number; error: NotificationServiceError | null }> {
  if (!supabase) {
    return {
      count: 0,
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      count: 0,
      error: { message: '用户未登录' },
    };
  }

  try {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('read', false);

    if (error) {
      console.error('[Notification] 获取未读通知数量失败:', error);
      return {
        count: 0,
        error: {
          message: error.message || '获取未读通知数量失败',
          code: error.code,
        },
      };
    }

    return { count: count || 0, error: null };
  } catch (error: any) {
    console.error('[Notification] 获取未读通知数量异常:', error);
    return {
      count: 0,
      error: {
        message: error.message || '获取未读通知数量时发生未知错误',
      },
    };
  }
}

/**
 * 删除通知
 */
export async function deleteNotification(notificationId: string): Promise<{ error: NotificationServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('user_id', user.id);

    if (error) {
      console.error('[Notification] 删除通知失败:', error);
      return {
        error: {
          message: error.message || '删除通知失败',
          code: error.code,
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    console.error('[Notification] 删除通知异常:', error);
    return {
      error: {
        message: error.message || '删除通知时发生未知错误',
      },
    };
  }
}

/**
 * 标记通知为已读
 */
export async function markNotificationAsRead(notificationId: string): Promise<{ error: NotificationServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .eq('user_id', user.id);

    if (error) {
      console.error('[Notification] 标记通知为已读失败:', error);
      return {
        error: {
          message: error.message || '标记通知为已读失败',
          code: error.code,
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    console.error('[Notification] 标记通知为已读异常:', error);
    return {
      error: {
        message: error.message || '标记通知为已读时发生未知错误',
      },
    };
  }
}

/**
 * 标记所有通知为已读
 */
export async function markAllNotificationsAsRead(): Promise<{ error: NotificationServiceError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false);

    if (error) {
      console.error('[Notification] 标记所有通知为已读失败:', error);
      return {
        error: {
          message: error.message || '标记所有通知为已读失败',
          code: error.code,
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    console.error('[Notification] 标记所有通知为已读异常:', error);
    return {
      error: {
        message: error.message || '标记所有通知为已读时发生未知错误',
      },
    };
  }
}

/**
 * 订阅实时通知
 */
export function subscribeToNotifications(
  callback: (notification: Notification) => void
): () => void {
  if (!supabase) {
    console.warn('[Notification] Supabase 未配置，无法订阅实时通知');
    return () => {};
  }

  const channel = supabase
    .channel('notifications')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
      },
      (payload) => {
        callback(payload.new as Notification);
      }
    )
    .subscribe();

  return () => {
    if (supabase) supabase.removeChannel(channel);
  };
}
